import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Calendar } from "lucide-react";
import { useIndices } from "@/hooks/useIndices";
import { formatPct, annualFromDaily, formatMonthYear } from "@/utils/formatters";

interface IndiceDisplay {
  name: string;
  value: string;
  date: string;
  isStale: boolean;
}

interface IndexData {
  value: string;
  date: string;
  isStale: boolean;
}

const IndicesEconomicosCard = () => {
  const { getAllStatus, loading } = useIndices();
  const [indices, setIndices] = useState<Record<string, IndexData>>({});

  // Função para verificar se o índice está desatualizado
  const isStale = (date: string, seriesType: 'daily' | 'monthly'): boolean => {
    if (!date) return true;
    
    const lastUpdate = new Date(date);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24));
    
    return (seriesType === 'daily' && diffDays > 3) || 
           (seriesType === 'monthly' && diffDays > 45);
  };

  // Formatar data para formato brasileiro
  const formatDate = (dateString: string): string => {
    if (!dateString) return "N/A";
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return "N/A";
    }
  };

  // Carregar dados dos índices diretamente da Edge Function
  useEffect(() => {
    const loadIndices = async () => {
      try {
        // Busca todos os índices de uma vez via getAllSeriesStatus (que agora usa Edge Function)
        const allData = await getAllStatus();
        
        // Debug log para verificar valores brutos e conversões
        console.table({
          'meta_%_a.a.': allData['SELIC_META_AA']?.value,       // SELIC Meta (se disponível)
          '11_raw_%_a.d.': allData[11]?.value,                  // ~0.04–0.08
          '11_%_a.a._fb': allData[11] ? annualFromDaily(allData[11].value) : null, // ~14–17
          '4390_raw_%_a.m.': allData[4390]?.value,              // ~1.0–1.3
          'IPCA': allData[433]?.value,                          // ~0-2
          'INPC': allData[188]?.value,                          // ~0-2
          'TR': allData[226]?.value                             // ~0-1
        });
        
        // SELIC a.a. - usar Meta SELIC (1178) com fallback para anualização da série diária (11)
        const selicMetaData = allData['SELIC_META_AA'];
        const selicDiariaData = allData[11];
        
        let selicAAValue: string;
        let selicAADate: string;
        let selicAAIsStale: boolean;
        
        if (selicMetaData) {
          // Usar Meta SELIC (preferencial)
          selicAAValue = formatPct(selicMetaData.value, 2) + " a.a.";
          selicAADate = selicMetaData.date;
          selicAAIsStale = isStale(selicMetaData.date, 'monthly'); // Meta é mensal
        } else if (selicDiariaData) {
          // Fallback: anualizar série diária
          selicAAValue = formatPct(annualFromDaily(selicDiariaData.value), 2) + " a.a.";
          selicAADate = selicDiariaData.date;
          selicAAIsStale = isStale(selicDiariaData.date, 'daily');
        } else {
          selicAAValue = "0,00% a.a.";
          selicAADate = "";
          selicAAIsStale = true;
        }
        
        const selicDiaria: IndexData = {
          value: selicAAValue,
          date: selicAADate,
          isStale: selicAAIsStale
        };

        // SELIC Mensal (série 4390) - usar % a.m. direto
        const selicMensalData = allData[4390];
        const selicMensal: IndexData = {
          value: selicMensalData ? formatPct(selicMensalData.value, 2) + " a.m." : "0,00% a.m.",
          date: selicMensalData ? selicMensalData.date : "",
          isStale: selicMensalData ? isStale(selicMensalData.date, 'monthly') : true
        };

        // IPCA (série 433)
        const ipcaData = allData[433];
        const ipca: IndexData = {
          value: ipcaData ? formatPct(ipcaData.value, 2) : "0,00%", 
          date: ipcaData ? ipcaData.date : "",
          isStale: ipcaData ? isStale(ipcaData.date, 'monthly') : true
        };

        // INPC (série 188)
        const inpcData = allData[188];
        const inpc: IndexData = {
          value: inpcData ? formatPct(inpcData.value, 2) : "0,00%",
          date: inpcData ? inpcData.date : "",
          isStale: inpcData ? isStale(inpcData.date, 'monthly') : true
        };

        // TR (série 226)
        const trData = allData[226];
        const tr: IndexData = {
          value: trData ? formatPct(trData.value, 2) : "0,00%",
          date: trData ? trData.date : "",
          isStale: trData ? isStale(trData.date, 'monthly') : true
        };

        setIndices({ selicDiaria, selicMensal, ipca, inpc, tr });
      } catch (error) {
        console.error('Erro ao carregar índices:', error);
        // Em caso de erro, mostra valores padrão em vez de dummy
        setIndices({
          selicDiaria: { value: "0,00% a.a.", date: "", isStale: true },
          selicMensal: { value: "0,00% a.m.", date: "", isStale: true },
          ipca: { value: "0,00%", date: "", isStale: true },
          inpc: { value: "0,00%", date: "", isStale: true },
          tr: { value: "0,00%", date: "", isStale: true }
        });
      }
    };

    loadIndices();
  }, [getAllStatus]);

  // Processar dados para exibição - 5 índices com códigos técnicos
  const indicesDisplay: IndiceDisplay[] = [
    {
      name: "SELIC a.a. (Meta/11)", 
      value: indices.selicDiaria?.value || "0,00% a.a.",
      date: formatDate(indices.selicDiaria?.date || ""),
      isStale: indices.selicDiaria?.isStale || true
    },
    {
      name: "SELIC Mensal (4390)", 
      value: indices.selicMensal?.value || "0,00% a.m.",
      date: indices.selicMensal?.date ? formatMonthYear(indices.selicMensal.date) : "N/A",
      isStale: indices.selicMensal?.isStale || true
    },
    {
      name: "IPCA (433)", 
      value: indices.ipca?.value || "0,00%",
      date: indices.ipca?.date ? formatMonthYear(indices.ipca.date) : "N/A",
      isStale: indices.ipca?.isStale || true
    },
    {
      name: "INPC (188)",
      value: indices.inpc?.value || "0,00%", 
      date: indices.inpc?.date ? formatMonthYear(indices.inpc.date) : "N/A",
      isStale: indices.inpc?.isStale || true
    },
    {
      name: "TR (226)",
      value: indices.tr?.value || "0,00%",
      date: indices.tr?.date ? formatMonthYear(indices.tr.date) : "N/A",
      isStale: indices.tr?.isStale || true
    }
  ];

  if (loading) {
    return (
      <Card className="mb-4">
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="text-sm text-muted-foreground">Carregando índices econômicos do BCB...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <TrendingUp className="h-5 w-5" />
          Índices Econômicos Atualizados (BCB)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {indicesDisplay.map((indice) => (
            <div key={indice.name} className="text-center space-y-1">
              <Badge 
                variant={indice.isStale ? "secondary" : "default"}
                className="w-full justify-center text-xs font-medium truncate"
              >
                {indice.name}
              </Badge>
              <div className="space-y-1">
                <p className="font-semibold text-sm">
                  {indice.value}
                </p>
                <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  <span className="text-xs">{indice.date}</span>
                </div>
                {indice.isStale && (
                   <div className="text-xs text-amber-600">
                     Última atualização
                   </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default IndicesEconomicosCard;